import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { 
  FiEdit,
  FiSave,
  FiX,
  FiArrowLeft,
  FiFileText,
  FiClipboard,
  FiMapPin,
  FiCalendar,
  FiTruck,
  FiBox,
  FiHome,
  FiLayers,
  FiDownload,
  FiMail,
  FiPhone,
  FiClock,
  FiUser,
  FiDollarSign,
  FiCheck,
  FiTrash2,
  FiAlertCircle,
  FiMessageSquare
} from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { socketService } from '../../utils/socketService';

// Composant Badge pour afficher le statut
const Badge = ({ status }) => {
  const statusMap = {
    pending: { label: 'En attente', class: 'bg-amber-500/20 text-amber-400' },
    confirmed: { label: 'Confirmé', class: 'bg-blue-500/20 text-blue-400' },
    accepted: { label: 'Accepté', class: 'bg-green-500/20 text-green-400' },
    rejected: { label: 'Refusé', class: 'bg-red-500/20 text-red-400' },
    expired: { label: 'Expiré', class: 'bg-gray-500/20 text-gray-400' },
  };

  const statusInfo = statusMap[status] || { label: status, class: 'bg-gray-500/20 text-gray-400' };

  return (
    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusInfo.class}`}>
      {statusInfo.label}
    </span>
  );
};

// Composant de chargement
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
    </div>
  );
}

// Fonction pour formater la date
const formatDate = (dateString) => {
  if (!dateString) return 'Non spécifiée';
  return format(parseISO(dateString), 'PPP', { locale: fr });
};

// Fonction pour formater la date et l'heure
const formatDateTime = (dateString) => {
  if (!dateString) return 'Non spécifiée';
  return format(parseISO(dateString), "PPP 'à' HH'h'mm", { locale: fr });
};

// Fonction pour formater le numéro de téléphone
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  // Supprimer tous les caractères non numériques
  const cleaned = phoneNumber.replace(/\D/g, '');
  // Formater selon le format tunisien
  if (cleaned.length === 8) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3');
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{2})(\d{3})(\d{3})/, '+$1 $2 $3 $4');
  } else if (cleaned.length === 12) {
    return cleaned.replace(/(\d{3})(\d{2})(\d{3})(\d{2})(\d{2})/, '+$1 $2 $3 $4 $5');
  }
  return phoneNumber; // Retourne le numéro original si le format n'est pas reconnu
};

// Fonction pour ajouter un en-tête de page
const addPageHeader = (doc, quoteData, pageWidth, margin) => {
  // Couleurs de la marque
  const primaryColor = [44, 62, 80]; // Bleu foncé
  const accentColor = [245, 158, 11]; // Orange
  
  // En-tête avec fond coloré
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 80, 'F');
  
  // Logo et nom de l'entreprise
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('LIBERTY DÉMÉNAGEMENT', margin, 25);
  
  // Slogan
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);
  doc.text('Votre partenaire de confiance pour un déménagement sans souci', margin, 32);
  
  // Bandeau d'accent
  doc.setFillColor(...accentColor);
  doc.rect(0, 80, pageWidth, 4, 'F');
  
  // Numéro de devis et date
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text(`DEVIS N°${quoteData.id || '0000'}`, pageWidth - margin, 40, { align: 'right' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Date: ${format(new Date(quoteData.created_at || new Date()), 'dd/MM/yyyy', { locale: fr })}`, 
           pageWidth - margin, 46, { align: 'right' });
  
  return 90; // Retourne la position Y après l'en-tête
};

// Fonction pour ajouter un pied de page
const addPageFooter = (doc, pageWidth, pageHeight, margin) => {
  const primaryColor = [44, 62, 80];
  const textColor = 100;
  
  // Ligne de séparation
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(margin, pageHeight - 30, pageWidth - margin, pageHeight - 30);
  
  // Texte du pied de page
  doc.setFontSize(8);
  doc.setTextColor(textColor);
  doc.setFont('helvetica', 'normal');
  
  // Coordonnées
  doc.text('Liberty Déménagement', margin, pageHeight - 20);
  doc.text('123 Avenue des Champs-Élysées', margin, pageHeight - 15);
  doc.text('75008 Paris, France', margin, pageHeight - 10);
  doc.text('contact@liberty-demenagement.com', margin, pageHeight - 5);
  
  // Mentions légales
  doc.text('SIRET: 123 456 789 00012 • RCS Paris 123 456 789', pageWidth - margin, pageHeight - 20, { align: 'right' });
  doc.text('TVA intracommunautaire: FR12345678901', pageWidth - margin, pageHeight - 15, { align: 'right' });
  doc.text('www.liberty-demenagement.com', pageWidth - margin, pageHeight - 10, { align: 'right' });
  doc.text('Tél: 01 23 45 67 89', pageWidth - margin, pageHeight - 5, { align: 'right' });
};

// Fonction pour ajouter un titre de section
const addSectionTitle = (doc, title, yPos, pageWidth, margin) => {
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(44, 62, 80);
  doc.text(title.toUpperCase(), margin, yPos);
  
  // Ligne de soulignement
  doc.setDrawColor(245, 158, 11);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos + 1, margin + 40, yPos + 1);
  
  return yPos + 8;
};

// Fonction pour formater le montant
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'Sur devis';
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }).format(amount);
};

// Fonction pour exporter le devis en PDF
const exportToPdf = (quoteData) => {
  if (!quoteData) return;

  // Créer un nouveau document PDF
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = addPageHeader(doc, quoteData, pageWidth, margin);

  // Espacement après l'en-tête
  yPos += 5;

  // Section Informations client
  yPos = addSectionTitle(doc, 'Client', yPos, pageWidth, margin);
  
  // Encadré d'informations client
  const clientX = margin;
  const clientY = yPos;
  const clientWidth = pageWidth - 2 * margin;
  const clientHeight = 40;
  
  // Fond de l'encadré
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(clientX, clientY, clientWidth, clientHeight, 3, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(clientX, clientY, clientWidth, clientHeight, 3, 3, 'S');
  
  // Contenu de l'encadré
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(44, 62, 80);
  doc.text('Nom & Prénom:', clientX + 10, clientY + 10);
  doc.text('Email:', clientX + 10, clientY + 20);
  doc.text('Téléphone:', clientX + 10, clientY + 30);
  
  doc.setFont('helvetica', 'normal');
  doc.text(`${quoteData.client_firstname || ''} ${quoteData.client_lastname || 'Non spécifié'}`, clientX + 35, clientY + 10);
  doc.text(quoteData.client_email || 'Non spécifié', clientX + 35, clientY + 20);
  doc.text(quoteData.client_phone ? formatPhoneNumber(quoteData.client_phone) : 'Non spécifié', clientX + 35, clientY + 30);
  
  yPos += clientHeight + 15;

  // Section Détails du déménagement
  yPos = addSectionTitle(doc, 'Détails du déménagement', yPos, pageWidth, margin);
  
  // Encadré des détails
  const detailsX = margin;
  const detailsY = yPos;
  const detailsWidth = pageWidth - 2 * margin;
  const detailsHeight = 65;
  
  // Fond de l'encadré
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(detailsX, detailsY, detailsWidth, detailsHeight, 3, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(detailsX, detailsY, detailsWidth, detailsHeight, 3, 3, 'S');
  
  // Colonnes pour une meilleure organisation
  const col1 = detailsX + 10;
  const col2 = col1 + 80;
  const col3 = col2 + 80;
  
  // Style des labels
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  
  // Ligne 1
  doc.text('Date prévue', col1, detailsY + 12);
  doc.text('Type de logement', col2, detailsY + 12);
  doc.text('Volume estimé', col3, detailsY + 12);
  
  // Valeurs ligne 1
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(format(new Date(quoteData.move_date || new Date()), 'EEEE d MMMM yyyy', { locale: fr }), col1, detailsY + 22);
  doc.text(quoteData.housing_type ? quoteData.housing_type.charAt(0).toUpperCase() + quoteData.housing_type.slice(1) : 'Non spécifié', col2, detailsY + 22);
  doc.text(quoteData.volume_m3 ? `${quoteData.volume_m3} m³` : 'Non spécifié', col3, detailsY + 22);
  
  // Ligne 2
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Type de camion', col1, detailsY + 40);
  doc.text('Étage', col2, detailsY + 40);
  doc.text('Ascenseur', col3, detailsY + 40);
  
  // Valeurs ligne 2
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(
    quoteData.truck_type === 'small' ? 'Petit camion (10-15 m³)' :
    quoteData.truck_type === 'medium' ? 'Camion moyen (16-25 m³)' :
    quoteData.truck_type === 'large' ? 'Grand camion (26-40 m³)' :
    quoteData.truck_type === 'xl' ? 'Camion XXL (40+ m³)' :
    'Non spécifié',
    col1, detailsY + 50
  );
  
  // Affichage de l'étage
  const floorText = 
    quoteData.floor === 0 ? 'Rez-de-chaussée' :
    quoteData.floor === 1 ? '1er étage' :
    quoteData.floor ? `${quoteData.floor}e étage` :
    'Non spécifié';
  doc.text(floorText, col2, detailsY + 50);
  
  // Affichage de l'ascenseur
  const elevatorText = 
    quoteData.has_elevator === true ? 'Oui' :
    quoteData.has_elevator === false ? 'Non' :
    'Non spécifié';
  doc.text(elevatorText, col3, detailsY + 50);
  
  yPos += detailsHeight + 15;

  // Section Adresses
  yPos = addSectionTitle(doc, 'Adresses', yPos, pageWidth, margin);
  
  // Conteneur pour les deux adresses côte à côte
  const addressContainerY = yPos;
  const addressWidth = (pageWidth - 2 * margin - 20) / 2;
  
  // Adresse de départ
  doc.setFillColor(255, 247, 237); // Fond légèrement orangé
  doc.roundedRect(margin, addressContainerY, addressWidth, 60, 3, 3, 'F');
  doc.setDrawColor(254, 215, 170);
  doc.roundedRect(margin, addressContainerY, addressWidth, 60, 3, 3, 'S');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(194, 65, 12);
  doc.text('ADRESSE DE DÉPART', margin + 10, addressContainerY + 12);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  
  // Découpage de l'adresse pour qu'elle tienne dans la largeur
  const fromAddressLines = doc.splitTextToSize(quoteData.from_address || 'Non spécifiée', addressWidth - 20);
  doc.text(fromAddressLines, margin + 10, addressContainerY + 25);
  
  // Adresse d'arrivée
  doc.setFillColor(240, 249, 255); // Fond légèrement bleuté
  doc.roundedRect(margin + addressWidth + 20, addressContainerY, addressWidth, 60, 3, 3, 'F');
  doc.setDrawColor(191, 219, 254);
  doc.roundedRect(margin + addressWidth + 20, addressContainerY, addressWidth, 60, 3, 3, 'S');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 58, 138);
  doc.text('ADRESSE D\'ARRIVÉE', margin + addressWidth + 30, addressContainerY + 12);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  
  // Découpage de l'adresse pour qu'elle tienne dans la largeur
  const toAddressLines = doc.splitTextToSize(quoteData.to_address || 'Non spécifiée', addressWidth - 20);
  doc.text(toAddressLines, margin + addressWidth + 30, addressContainerY + 25);
  
  yPos += 75; // Ajustement de l'espacement après les adresses

  // Section Services inclus
  const services = [];
  if (quoteData.services) {
    if (quoteData.services.cartons) services.push('Cartons fournis');
    if (quoteData.services.emballage) services.push('Service d\'emballage');
    if (quoteData.services.nettoyage) services.push('Service de nettoyage');
    if (quoteData.services.gardeMeubles) services.push('Garde-meubles');
    if (quoteData.services.montageMeubles) services.push('Montage/Démontage de meubles');
  }
  
  if (services.length > 0) {
    yPos = addSectionTitle(doc, 'Services inclus', yPos, pageWidth, margin);
    
    // Encadré des services
    const servicesX = margin;
    const servicesY = yPos;
    const servicesWidth = pageWidth - 2 * margin;
    const servicesHeight = 15 + services.length * 7;
    
    // Style de l'encadré
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(servicesX, servicesY, servicesWidth, servicesHeight, 3, 3, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(servicesX, servicesY, servicesWidth, servicesHeight, 3, 3, 'S');
    
    // Icônes et texte des services
    services.forEach((service, index) => {
      const y = servicesY + 12 + (index * 7);
      
      // Icône de validation
      doc.setFillColor(16, 185, 129);
      doc.circle(servicesX + 12, y - 2, 2, 'F');
      
      // Texte du service
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(service, servicesX + 20, y);
    });
    
    yPos += servicesHeight + 10;
  }

  // Section Montant total
  yPos = addSectionTitle(doc, 'Montant total', yPos, pageWidth, margin);
  
  // Encadré du montant total
  const totalX = margin;
  const totalY = yPos;
  const totalWidth = pageWidth - 2 * margin;
  const totalHeight = 30;
  
  // Style de l'encadré
  doc.setFillColor(236, 253, 245);
  doc.roundedRect(totalX, totalY, totalWidth, totalHeight, 3, 3, 'F');
  doc.setDrawColor(167, 243, 208);
  doc.roundedRect(totalX, totalY, totalWidth, totalHeight, 3, 3, 'S');
  
  // Libellé
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(6, 95, 70);
  doc.text('MONTANT TOTAL TTC', totalX + 15, totalY + 12);
  
  // Montant
  doc.setFontSize(18);
  doc.text(
    formatCurrency(quoteData.total_price), 
    pageWidth - margin - 15, 
    totalY + 12,
    { align: 'right' }
  );
  
  // Conditions de paiement
  yPos += totalHeight + 20;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  
  const conditions = [
    '• Paiement à la signature du contrat',
    '• 30% d\'acompte à la commande',
    '• Solde à la livraison des biens',
    '• Moyens de paiement acceptés : espèces, chèque, virement bancaire, carte bancaire',
    '• Paiement échelonné possible sans frais sur demande'
  ];
  
  conditions.forEach((condition, index) => {
    doc.text(condition, margin, yPos + (index * 5));
  });
  
  // Mentions légales
  yPos += conditions.length * 5 + 15;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(100);
  
  const mentions = [
    'Ce devis est valable 30 jours à compter de sa date d\'émission.',
    'Les prix sont exprimés en euros et toutes taxes comprises (TVA 20%).',
    'Tout retard de paiement entraînera des pénalités de retard au taux légal en vigueur.',
    'En cas d\'annulation, l\'acompte ne sera pas remboursé.'
  ];
  
  mentions.forEach((mention, index) => {
    doc.text(mention, margin, yPos + (index * 4));
  });
  
  // Signature
  yPos += mentions.length * 4 + 15;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(44, 62, 80);
  doc.text('Fait à Paris, le ' + format(new Date(), 'dd/MM/yyyy', { locale: fr }), margin, yPos);
  doc.text('Pour Liberty Déménagement', margin, yPos + 30);
  
  // Ligne de signature
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos + 40, margin + 100, yPos + 40);
  
  // Pied de page
  addPageFooter(doc, pageWidth, pageHeight, margin);

  // Sauvegarder le PDF
  doc.save(`devis-liberty-${quoteData.id || 'new'}.pdf`);
};

// Composant pour afficher une icône de fichier en fonction du type
const FileIcon = ({ type }) => {
  const iconMap = {
    'application/pdf': <FiFileText className="text-red-500" />,
    'application/msword': <FiFileText className="text-blue-500" />,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': <FiFileText className="text-blue-500" />,
    'application/vnd.ms-excel': <FiFileText className="text-green-500" />,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FiFileText className="text-green-500" />,
    'image/jpeg': <FiFileText className="text-purple-500" />,
    'image/png': <FiFileText className="text-purple-500" />,
  };

  return iconMap[type] || <FiFileText className="text-gray-400" />;
};

export default function ClientQuoteDetails() {
  const { id } = useParams();
  console.log('ID extrait des paramètres d\'URL:', id);
  console.log('URL complète:', window.location.href);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  
  // États pour la négociation
  const [offers, setOffers] = useState([]);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [counterOffer, setCounterOffer] = useState('');
  const [negotiationMessage, setNegotiationMessage] = useState('');
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);
  const [showNegotiationForm, setShowNegotiationForm] = useState(false);
  const offersCountRef = useRef(0);

  useEffect(() => {
    offersCountRef.current = Array.isArray(offers) ? offers.length : 0;
  }, [offers]);

  // Charger les offres de négociation
  const loadOffers = useCallback(async () => {
    try {
      console.log('Chargement des offres pour le devis ID:', id);
      
      // Charger les offres depuis l'API
      console.log('Tentative de chargement des offres depuis l\'API...');
      const response = await api.getQuoteOffers(id);
      console.log('Réponse API des offres:', response);
      
      if (response) {
        const apiOffers = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.offers)
              ? response.offers
              : Array.isArray(response?.data?.offers)
                ? response.data.offers
                : [];
        console.log('Offres chargées depuis l\'API:', apiOffers);
        
        if (apiOffers.length > 0) {
          // Vérifier s'il y a de nouvelles offres
          const oldOffersCount = offersCountRef.current;
          const isNewOffer = apiOffers.length > oldOffersCount && oldOffersCount > 0;
          
          setOffers(apiOffers);
          
          // Trouver une offre en attente ou prendre la première disponible
          const pendingOffer = apiOffers.find(o => o.status === 'pending' && o.user_type === 'mover') || apiOffers[0];
          if (pendingOffer) {
            setCurrentOffer(pendingOffer);
            setShowNegotiationForm(true);
            
            // Afficher une notification pour les nouvelles offres
            if (isNewOffer) {
              const newOffersCount = apiOffers.length - oldOffersCount;
              toast.info(`Nouvelle${newOffersCount > 1 ? 's' : ''} proposition${newOffersCount > 1 ? 's' : ''} reçue${newOffersCount > 1 ? 's' : ''} !`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
              });
            }
          }
        } else {
          // Aucune offre trouvée
          setOffers([]);
          setCurrentOffer(null);
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des offres:', err);
      console.error('Détails de l\'erreur:', err.response?.data || err.message);
      toast.error('Erreur lors du chargement des offres. Veuillez réessayer.');
    }
  }, [id]);
  
  // Chargement initial des offres (rafraîchissement via WebSocket / negotiation).
  useEffect(() => {
    if (!id) return undefined;
    loadOffers();
    return undefined;
  }, [id, loadOffers]);

  // Fallback temps réel: certains flux backend émettent price_proposed/notification
  // sans passer par le namespace de négociation. On écoute ici pour forcer le refresh.
  useEffect(() => {
    if (!id) return undefined;

    const isSameMission = (payload) => {
      const missionId = payload?.missionId || payload?.data?.missionId || payload?.payload?.missionId;
      return missionId && String(missionId) === String(id);
    };

    const refreshFromRealtime = () => {
      loadOffers().catch(() => {});
      // Mettre aussi à jour le devis (statut under_negotiation, prix, etc.)
      api.getQuoteById(id)
        .then((response) => {
          const quoteData = response?.data || response;
          if (quoteData) setData((prev) => ({ ...prev, ...quoteData }));
        })
        .catch(() => {});
    };

    const onPriceProposed = (payload) => {
      if (!isSameMission(payload)) return;
      toast.info('Nouvelle proposition de prix reçue');
      refreshFromRealtime();
    };

    const onNotification = (payload) => {
      if (!payload || payload.type !== 'price_proposed') return;
      if (!isSameMission(payload)) return;
      toast.info('Nouvelle proposition de prix reçue');
      refreshFromRealtime();
    };

    socketService.connect().catch(() => {});
    const disposers = [
      socketService.on('price_proposed', onPriceProposed),
      socketService.on('notification', onNotification),
    ];

    return () => {
      disposers.forEach((dispose) => {
        if (typeof dispose === 'function') dispose();
      });
    };
  }, [id, loadOffers]);

  // Namespace /negotiation : même canal que MissionDetails (web) ; l’app déménageur envoie aussi negotiation:update après propose-price.
  useEffect(() => {
    if (!id) return undefined;

    const refresh = () => {
      loadOffers().catch(() => {});
      api
        .getQuoteById(id)
        .then((response) => {
          const quoteData = response?.data || response;
          if (quoteData) setData((prev) => ({ ...prev, ...quoteData }));
        })
        .catch(() => {});
    };

    const cleanup = api.connectToNegotiationUpdates(id, refresh);
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [id, loadOffers]);

  // Charger les données du devis
  useEffect(() => {
    const loadQuote = async () => {
      if (!id) {
        console.error('Aucun ID de devis fourni');
        setError('Aucun identifiant de devis fourni');
        setLoading(false);
        return;
      }
      
      try {
        console.log('Début du chargement du devis et des offres');
        setLoading(true);
        setError(null);
        
        // Charger les données du devis
        const response = await api.getQuoteById(id);
        const quoteData = response.data || response;
        console.log('Données du devis chargées:', quoteData);
        
        // Mettre à jour les données du devis
        setData(quoteData);
        
        // Si le statut le permet, charger les offres
        if (quoteData.status === 'pending' || quoteData.status === 'under_negotiation') {
          console.log('Chargement des offres pour le devis:', id);
          await loadOffers();
        } else {
          console.log('Le statut du devis ne nécessite pas le chargement des offres:', quoteData.status);
        }
        
      } catch (err) {
        console.error('Erreur lors du chargement du devis:', err);
        const errorMessage = err?.response?.data?.message || err.message || 'Erreur inconnue';
        setError(`Impossible de charger les détails du devis: ${errorMessage}`);
        toast.error(`Erreur lors du chargement du devis: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };
    
    loadQuote();
  }, [id]);

  // Gestion des changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateQuote(id, data);
      setEditing(false);
      toast.success('Devis mis à jour avec succès!');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du devis:', err);
      toast.error('Erreur lors de la mise à jour du devis');
    } finally {
      setSaving(false);
    }
  };

  // Supprimer le devis
  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.deleteQuote(id);
      toast.success('Devis supprimé avec succès!');
      navigate('/client/quotes');
    } catch (err) {
      console.error('Erreur lors de la suppression du devis:', err);
      toast.error('Erreur lors de la suppression du devis');
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  // Rendu du contenu principal
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="flex items-center justify-center gap-3 text-gray-300 bg-gray-800 p-8 rounded-2xl border border-gray-700">
          <div className="animate-spin h-8 w-8 border-3 border-gray-600 border-t-amber-400 rounded-full"></div>
          <span className="text-lg">Chargement des détails du devis...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-3 text-red-400 mb-4">
              <FiAlertCircle className="h-6 w-6" />
              <h3 className="text-xl font-semibold">Erreur</h3>
            </div>
            <p className="text-gray-300 mb-6">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
            >
              <FiArrowLeft className="h-4 w-4" />
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Rendu des détails du devis
  const renderQuoteDetails = () => (
    <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-700 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FiFileText className="h-5 w-5 text-amber-400" />
          Détails du devis
        </h3>
        <button
          onClick={() => exportToPdf(data)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
          title="Exporter en PDF"
        >
          <FiDownload className="h-4 w-4" />
          <span className="hidden sm:inline">Exporter</span>
        </button>
      </div>
      <div className="p-6">
        <div className="space-y-8">
          {/* Section Adresses */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Adresses</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Adresse de départ */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Départ</dt>
                <dd className="text-white">
                  {editing ? (
                    <input
                      type="text"
                      name="from_address"
                      value={data.from_address || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full bg-gray-700 border border-gray-600 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                      placeholder="Adresse de départ"
                    />
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiMapPin className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{data.from_address || 'Non spécifiée'}</span>
                    </div>
                  )}
                </dd>
              </div>

              {/* Adresse d'arrivée */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Arrivée</dt>
                <dd className="text-white">
                  {editing ? (
                    <input
                      type="text"
                      name="to_address"
                      value={data.to_address || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full bg-gray-700 border border-gray-600 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                      placeholder="Adresse d'arrivée"
                    />
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiMapPin className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{data.to_address || 'Non spécifiée'}</span>
                    </div>
                  )}
                </dd>
              </div>
            </div>
          </div>

          {/* Section Déménagement */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Déménagement</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Date de déménagement */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Date prévue</dt>
                <dd className="text-white">
                  {editing ? (
                    <input
                      type="date"
                      name="move_date"
                      value={data.move_date ? data.move_date.split('T')[0] : ''}
                      onChange={handleChange}
                      className="mt-1 block w-full bg-gray-700 border border-gray-600 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiCalendar className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">{formatDate(data.move_date) || 'Non spécifiée'}</span>
                    </div>
                  )}
                </dd>
              </div>

              {/* Type de logement */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Type de logement</dt>
                <dd className="text-white">
                  {editing ? (
                    <select
                      name="housing_type"
                      value={data.housing_type || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full bg-gray-700 border border-gray-600 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="appartement">Appartement</option>
                      <option value="maison">Maison</option>
                      <option value="bureau">Bureau</option>
                      <option value="autre">Autre</option>
                    </select>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiHome className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">
                        {data.housing_type === 'appartement' && 'Appartement'}
                        {data.housing_type === 'maison' && 'Maison'}
                        {data.housing_type === 'bureau' && 'Bureau'}
                        {data.housing_type === 'autre' && 'Autre type de logement'}
                        {!['appartement', 'maison', 'bureau', 'autre'].includes(data.housing_type) && 'Non spécifié'}
                      </span>
                    </div>
                  )}
                </dd>
              </div>

              {/* Étage */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Étage</dt>
                <dd className="text-white">
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        name="floor"
                        value={data.floor || ''}
                        onChange={handleChange}
                        min="0"
                        className="block w-full bg-gray-700 border border-gray-600 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                        placeholder="0 pour RDC"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiLayers className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">
                        {data.floor === 0 ? 'Rez-de-chaussée' : 
                         data.floor === 1 ? '1er étage' : 
                         data.floor ? `${data.floor}e étage` : 'Non spécifié'}
                      </span>
                    </div>
                  )}
                </dd>
              </div>

              {/* Ascenseur */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Ascenseur</dt>
                <dd className="text-white">
                  {editing ? (
                    <div className="flex items-center gap-4 mt-2">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="has_elevator"
                          checked={data.has_elevator === true}
                          onChange={() => setData({...data, has_elevator: true})}
                          className="h-4 w-4 text-amber-500 focus:ring-amber-500 border-gray-600"
                        />
                        <span className="ml-2 text-gray-300">Oui</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="has_elevator"
                          checked={data.has_elevator === false}
                          onChange={() => setData({...data, has_elevator: false})}
                          className="h-4 w-4 text-amber-500 focus:ring-amber-500 border-gray-600"
                        />
                        <span className="ml-2 text-gray-300">Non</span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiLayers className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">
                        {data.has_elevator === true ? 'Oui' : 
                         data.has_elevator === false ? 'Non' : 'Non spécifié'}
                      </span>
                    </div>
                  )}
                </dd>
              </div>
            </div>
          </div>

          {/* Section Véhicule */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Véhicule</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Type de camion */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Type de camion</dt>
                <dd className="text-white">
                  {editing ? (
                    <select
                      name="truck_type"
                      value={data.truck_type || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full bg-gray-700 border border-gray-600 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="small">Petit camion (10-15 m³)</option>
                      <option value="medium">Camion moyen (16-25 m³)</option>
                      <option value="large">Grand camion (26-40 m³)</option>
                      <option value="xl">Camion XXL (40+ m³)</option>
                    </select>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiTruck className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">
                        {data.truck_type === 'small' && 'Petit camion (10-15 m³)'}
                        {data.truck_type === 'medium' && 'Camion moyen (16-25 m³)'}
                        {data.truck_type === 'large' && 'Grand camion (26-40 m³)'}
                        {data.truck_type === 'xl' && 'Camion XXL (40+ m³)'}
                        {!['small', 'medium', 'large', 'xl'].includes(data.truck_type) && 'Non spécifié'}
                      </span>
                    </div>
                  )}
                </dd>
              </div>

              {/* Volume estimé */}
              <div className="space-y-1">
                <dt className="text-sm font-medium text-gray-400">Volume estimé</dt>
                <dd className="text-white">
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        name="volume_m3"
                        value={data.volume_m3 || ''}
                        onChange={handleChange}
                        min="1"
                        step="0.1"
                        className="block w-24 bg-gray-700 border border-gray-600 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                      />
                      <span className="text-gray-400">mètres cubes (m³)</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-gray-700/50 rounded-lg">
                      <FiBox className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">
                        {data.volume_m3 ? `${data.volume_m3} m³` : 'Non spécifié'}
                      </span>
                    </div>
                  )}
                </dd>
              </div>
            </div>
          </div>

          {/* Section Statut */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Statut</h4>
            <div className="p-3 bg-gray-700/50 rounded-lg">
              <Badge status={data.status} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendu des services inclus
  const renderServices = () => {
    // Vérifier si data est défini
    if (!data || !data.services) return null;

    let services = [];
    
    try {
      // Gérer le cas où services est une chaîne JSON
      const servicesData = typeof data.services === 'string' 
        ? JSON.parse(data.services) 
        : data.services;
      
      // Si c'est un objet, le convertir en tableau de libellés
      if (servicesData && typeof servicesData === 'object' && !Array.isArray(servicesData)) {
        // Mappage des clés de service vers des libellés lisibles
        const serviceLabels = {
          cartons: 'Cartons fournis',
          emballage: 'Service d\'emballage',
          nettoyage: 'Service de nettoyage',
          gardeMeubles: 'Garde-meubles',
          montageMeubles: 'Montage/Démontage de meubles'
        };
        
        // Convertir l'objet en tableau de services actifs
        services = Object.entries(servicesData)
          .filter(([_, value]) => value === true)
          .map(([key]) => serviceLabels[key] || key);
      } 
      // Si c'est déjà un tableau, l'utiliser tel quel
      else if (Array.isArray(servicesData)) {
        services = servicesData;
      }
    } catch (error) {
      console.error('Erreur lors du traitement des services:', error);
      return (
        <div className="bg-gray-800 rounded-2xl border border-red-500/30 overflow-hidden">
          <div className="p-4 text-red-400">
            <p>Erreur lors du chargement des services</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FiClipboard className="h-5 w-5 text-amber-400" />
            Services inclus
          </h3>
        </div>
        <div className="p-6">
          {services.length > 0 ? (
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index} className="flex items-start group">
                  <div className="flex-shrink-0 h-5 w-5 mt-0.5">
                    <div className="h-2 w-2 rounded-full bg-amber-400 group-hover:animate-pulse"></div>
                  </div>
                  <p className="ml-3 text-gray-300">{service}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-400 italic">Aucun service spécifié</p>
          )}
        </div>
      </div>
    );
  };

  // Rendu des informations de contact
  const renderContactInfo = () => (
    <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-700">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FiUser className="h-5 w-5 text-amber-400" />
          Contact
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-6">
          {/* Informations du client */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <FiUser className="h-5 w-5 text-amber-400" />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-medium text-white">
                  {data.contact_name || 'Client non spécifié'}
                </h4>
                <p className="text-sm text-gray-400">Client</p>
              </div>
            </div>

            <div className="space-y-3 pl-14">
              {/* Téléphone */}
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                  <FiPhone className="h-5 w-5" />
                </div>
                <div>
                  {data.contact_phone ? (
                    <a 
                      href={`tel:${data.contact_phone}`}
                      className="text-amber-400 hover:text-amber-300 transition-colors"
                    >
                      {formatPhoneNumber(data.contact_phone)}
                    </a>
                  ) : (
                    <span className="text-gray-400">Non spécifié</span>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                  <FiMail className="h-5 w-5" />
                </div>
                <div>
                  {data.contact_email ? (
                    <a 
                      href={`mailto:${data.contact_email}`}
                      className="text-amber-400 hover:text-amber-300 transition-colors break-all"
                    >
                      {data.contact_email}
                    </a>
                  ) : (
                    <span className="text-gray-400">Non spécifié</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bouton de contact */}
          <div className="pt-2">
            <a
              href={`mailto:${data.contact_email || 'contact@liberty-demenagement.com'}?subject=Question%20concernant%20le%20devis%20${data.id}`}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
            >
              <FiMessageSquare className="h-4 w-4" />
              {data.contact_email ? 'Envoyer un email' : 'Contacter le support'}
            </a>
          </div>

          {/* Informations de support */}
          <div className="pt-4 border-t border-gray-700">
            <h4 className="text-sm font-medium text-white mb-3">Support Liberty Déménagement</h4>
            <div className="space-y-2 text-sm text-gray-400">
              <p>Du lundi au vendredi, de 9h à 18h</p>
              <p>
                <a href="tel:+33123456789" className="text-amber-400 hover:text-amber-300">
                  +33 1 23 45 67 89
                </a>
              </p>
              <p>
                <a href="mailto:support@liberty-demenagement.com" className="text-amber-400 hover:text-amber-300">
                  support@liberty-demenagement.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Gérer la soumission d'une contre-offre
  const handleSubmitCounterOffer = async () => {
    if (!counterOffer || isSubmittingOffer) return;
    
    try {
      setIsSubmittingOffer(true);
      const amount = parseFloat(counterOffer);
      
      if (isNaN(amount) || amount <= 0) {
        toast.error('Veuillez entrer un montant valide');
        return;
      }

      // Vérifier s'il y a déjà une offre en attente de l'utilisateur
      const existingPendingOffer = offers.find(offer => 
        offer.user_type === 'client' && 
        (offer.status === 'pending' || offer.status === 'under_negotiation') &&
        !offer.isOptimistic // Ne pas considérer les offres optimistes
      );

      if (existingPendingOffer) {
        toast.error('Vous avez déjà une offre en attente pour ce devis');
        return;
      }

      // Créer un ID unique pour l'offre temporaire
      const tempId = `temp-${Date.now()}`;
      
      // Créer un objet d'offre temporaire pour une mise à jour optimiste
      const tempOffer = {
        id: tempId,
        quote_id: id,
        amount: amount,
        status: 'pending',
        message: negotiationMessage || `Je propose ${amount} TND pour ce déménagement.`,
        user_type: 'client',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        isOptimistic: true, // Marquer comme offre optimiste
        first_name: 'Vous',
        last_name: '',
        email: ''
      };

      console.log('Soumission de l\'offre temporaire:', tempOffer);

      // Mise à jour optimiste de l'état local
      setOffers(prev => [tempOffer, ...prev]);
      setCurrentOffer(tempOffer);
      setCounterOffer('');
      setNegotiationMessage('');
      setShowNegotiationForm(false);
      
      // Envoyer la requête API
      try {
        const response = await api.submitQuoteOffer(id, {
          amount: amount,
          message: tempOffer.message,
          user_type: 'client'
        });

        console.log('Réponse du serveur:', response);

        // Mettre à jour l'état avec la réponse du serveur
        setOffers(prev => 
          prev.map(offer => 
            offer.id === tempId ? { ...response.data, isOptimistic: false } : offer
          )
        );
        
        // Mettre à jour l'offre courante avec les données du serveur
        if (response.data) {
          setCurrentOffer({ ...response.data, isOptimistic: false });
        }
        
        toast.success('Votre contre-proposition a été envoyée avec succès !');
      } catch (apiError) {
        console.error('Erreur API lors de la soumission de l\'offre:', apiError);
        // Annuler la mise à jour optimiste en cas d'erreur
        setOffers(prev => prev.filter(offer => offer.id !== tempId));
        
        // Si l'erreur indique qu'il y a déjà une offre en attente, on recharge les offres
        if (apiError.response?.data?.message?.includes('déjà une offre en attente')) {
          console.log('Rechargement des offres...');
          await loadOffers();
        }
        
        throw apiError;
      }
    } catch (err) {
      console.error('Erreur lors de la soumission de la contre-offre:', err);
      
      // Afficher un message d'erreur plus détaillé
      const errorMessage = err.response?.data?.message || 
                          (err.message.includes('Network Error') ? 
                            'Impossible de se connecter au serveur. Vérifiez votre connexion Internet.' : 
                            'Une erreur est survenue lors de l\'envoi de votre contre-proposition');
      
      toast.error(errorMessage, { autoClose: 5000 });
      
      // Réafficher le formulaire pour permettre une nouvelle tentative
      setShowNegotiationForm(true);
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  // Répondre à une offre (accepter/refuser)
  const handleRespondToOffer = async (status) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir ${status === 'accepted' ? 'accepter' : 'refuser'} cette offre ?`)) {
      return;
    }

    try {
      setIsSubmittingOffer(true);
      
      // Mise à jour optimiste de l'état local
      const updatedOffers = offers.map(offer => 
        offer.id === currentOffer.id 
          ? { ...offer, status: status }
          : offer
      );
      
      setOffers(updatedOffers);
      setCurrentOffer(prev => ({ ...prev, status: status }));
      
      // Envoyer la requête API
      const response = await api.respondToOffer(id, currentOffer.id, {
        status: status,
        message: status === 'accepted' ? 'J\'accepte votre offre.' : 'Je décline votre offre.'
      });
      
      // Mettre à jour avec la réponse du serveur
      setOffers(prev => 
        prev.map(offer => 
          offer.id === currentOffer.id 
            ? response.data 
            : offer
        )
      );
      setCurrentOffer(response.data);

      // Si l'offre est acceptée, mettre à jour le statut du devis
      if (status === 'accepted' && data) {
        setData(prev => ({
          ...prev,
          status: 'accepted',
          price_cents: Math.round(currentOffer.amount * 100)
        }));
      }

      setShowNegotiationForm(false);
      toast.success(`Offre ${status === 'accepted' ? 'acceptée' : 'refusée'} avec succès !`);
    } catch (err) {
      console.error('Erreur lors de la réponse à l\'offre:', err);
      toast.error(err.response?.data?.message || 'Une erreur est survenue lors de la réponse à l\'offre');
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  // Certains flux (mobile -> service-requests/propose-price) écrivent le prix sur quotes.price_cents
  // sans créer d'entrée dans offers. On expose donc ce prix côté UI client.
  const quoteProposedPrice =
    data?.price_cents != null && !Number.isNaN(Number(data.price_cents))
      ? Number(data.price_cents) / 100
      : null;

  // Rendu de la section de négociation
  const renderNegotiationSection = () => {
    if (!showNegotiationForm && !currentOffer) {
      return (
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Négocier le prix</h3>
            <p className="text-gray-400 mb-4">
              Vous souhaitez négocier le prix de ce devis ?
            </p>
            <button
              onClick={() => setShowNegotiationForm(true)}
              className="w-full px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
            >
              Faire une contre-proposition
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FiMessageSquare className="h-5 w-5 text-amber-400" />
            Négociation en cours
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Fallback: afficher le prix proposé depuis la mission/devis même sans historique offers */}
          {offers.length === 0 && quoteProposedPrice != null && quoteProposedPrice > 0 && (
            <div className="p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">
                    Déménageur - {quoteProposedPrice.toFixed(2)} TND
                  </div>
                  <p className="text-sm text-gray-300 mt-1">
                    Proposition reçue. En attente de votre réponse.
                  </p>
                </div>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                  En attente
                </span>
              </div>
            </div>
          )}

          {/* Historique des offres */}
          {offers.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-300">Historique des offres</h4>
              <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                {[...offers]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((offer) => (
                    <div 
                      key={offer.id}
                      className={`p-4 rounded-lg border ${
                        offer.status === 'accepted' ? 'border-green-500/30 bg-green-500/10' :
                        offer.status === 'rejected' ? 'border-red-500/30 bg-red-500/10' :
                        'border-yellow-500/30 bg-yellow-500/10'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">
                            {offer.user_type === 'mover' ? 'Déménageur' : 'Vous'} - {offer.amount} TND
                          </div>
                          {offer.message && (
                            <p className="text-sm text-gray-300 mt-1">{offer.message}</p>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(offer.created_at).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <span 
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            offer.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                            offer.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}
                        >
                          {offer.status === 'accepted' ? 'Acceptée' :
                           offer.status === 'rejected' ? 'Refusée' : 'En attente'}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Formulaire de contre-offre */}
          {showNegotiationForm && (
            <div className="pt-4 border-t border-gray-700">
              <h4 className="text-sm font-medium text-gray-300 mb-3">
                {currentOffer ? 'Faire une contre-proposition' : 'Faire une offre'}
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Montant (TND)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={counterOffer}
                    onChange={(e) => setCounterOffer(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Entrez un montant"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Message (optionnel)
                  </label>
                  <textarea
                    value={negotiationMessage}
                    onChange={(e) => setNegotiationMessage(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    rows="3"
                    placeholder="Expliquez votre proposition..."
                  />
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <button
                    onClick={handleSubmitCounterOffer}
                    disabled={!counterOffer || isSubmittingOffer}
                    className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmittingOffer ? 'Envoi en cours...' : 'Envoyer la proposition'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowNegotiationForm(false);
                      setCounterOffer('');
                      setNegotiationMessage('');
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Boutons d'action pour l'offre en cours */}
          {currentOffer?.status === 'pending' && currentOffer.user_type === 'mover' && (
            <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-700">
              <button
                onClick={() => handleRespondToOffer('accepted')}
                disabled={isSubmittingOffer}
                className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                Accepter l'offre
              </button>
              
              <button
                onClick={() => handleRespondToOffer('rejected')}
                disabled={isSubmittingOffer}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                Refuser l'offre
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Rendu des documents
  const renderDocuments = () => (
    <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-700">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FiFileText className="h-5 w-5 text-amber-400" />
          Documents
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-gray-700/50 rounded-xl hover:bg-gray-700/80 transition-colors">
            <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <FiFileText className="h-6 w-6 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">Devis #{id}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Créé le {formatDate(data.created_at)}
              </p>
            </div>
            <div className="flex-shrink-0">
              <a
                href={`/api/quotes/${id}/download`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-colors"
                download
              >
                <FiDownload className="h-3.5 w-3.5" />
                Télécharger
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendu du modal de confirmation de suppression
  const renderDeleteModal = () => (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${!confirmOpen && 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        </div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        
        <div className="inline-block align-bottom bg-gray-800 rounded-2xl px-6 pt-6 pb-6 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-8 border border-gray-700">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-500/10">
              <FiTrash2 className="h-8 w-8 text-red-500" />
            </div>
            <div className="mt-5">
              <h3 className="text-xl font-bold text-white">
                Supprimer le devis
              </h3>
              <div className="mt-3">
                <p className="text-gray-400">
                  Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est irréversible.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-8 flex flex-col sm:flex-row-reverse gap-3">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Suppression...
                </>
              ) : (
                <>
                  <FiTrash2 className="h-4 w-4" />
                  Supprimer définitivement
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="w-full px-4 py-2.5 text-gray-300 hover:text-white font-medium rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendu principal
  console.log('Rendu principal - Données du devis:', data);
  console.log('Statut du devis (valeur brute):', data?.status);
  console.log('Type du statut:', typeof data?.status);
  console.log('Vérification des statuts:', {
    isPending: data?.status === 'pending',
    isUnderNegotiation: data?.status === 'under_negotiation',
    statusIncludesPending: String(data?.status).includes('pending'),
    statusIncludesUnderNegotiation: String(data?.status).includes('under_negotiation')
  });
  console.log('Offres chargées:', offers);
  console.log('Afficher la section de négociation?', data?.status === 'pending' || data?.status === 'under_negotiation');
  
  return (
    <div className="min-h-screen bg-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* En-tête */}
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Devis #{data.id || id}</h1>
              <div className="flex items-center gap-4 mt-2 text-gray-400">
                <span className="flex items-center gap-1 text-sm">
                  <FiCalendar className="h-4 w-4" />
                  {formatDate(data.created_at)}
                </span>
                <Badge status={data.status} />
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {editing ? (
                <>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                  >
                    <FiEdit className="h-4 w-4" />
                    Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={deleting}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <FiTrash2 className="h-4 w-4" />
                    {deleting ? 'Suppression...' : 'Supprimer'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne de gauche - Détails du devis */}
          <div className="lg:col-span-2 space-y-6">
            {renderQuoteDetails()}
            {renderServices()}
          </div>

          {/* Colonne de droite - Informations complémentaires */}
          <div className="space-y-6">
            {/* Afficher la section de négociation s'il y a des offres
                ou un prix proposé stocké sur le devis */}
            {(offers.length > 0 || (quoteProposedPrice != null && quoteProposedPrice > 0) || showNegotiationForm) ? (
              renderNegotiationSection()
            ) : (
              // Afficher un bouton pour démarrer une négociation si le statut le permet
              (data?.status === 'pending' || data?.status === 'under_negotiation') && (
                <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Négocier le prix</h3>
                    <p className="text-gray-400 mb-4">
                      Vous souhaitez négocier le prix de ce devis ?
                    </p>
                    <button
                      onClick={() => setShowNegotiationForm(true)}
                      className="w-full px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                    >
                      Faire une contre-proposition
                    </button>
                  </div>
                </div>
              )
            )}
            {renderContactInfo()}
            {renderDocuments()}
          </div>
        </div>

        {renderDeleteModal()}
      </div>
    </div>
  );
}
